/**
 * The applicability expression language (§6.1).
 *
 * Deliberately tiny: flag names, `&&`, `||`, `!`, parentheses. No arithmetic, no
 * dates, no function calls. Parsed once at import time into an AST that is stored;
 * a stored string is never `eval`'d at runtime.
 *
 * Precedence: `!` binds tightest, then `&&`, then `||`.
 */

import { ContextExpressionError } from './errors.js';
import { APPLICABILITY_FLAGS } from './types.js';
import type { ApplicabilityFlag, ContextExpr, ContextProfile } from './types.js';

type TokenType = 'ident' | 'and' | 'or' | 'not' | 'lparen' | 'rparen' | 'eof';

interface Token {
  readonly type: TokenType;
  readonly value: string;
  readonly position: number;
}

const IDENT_START = /[A-Za-z_]/;
const IDENT_PART = /[A-Za-z0-9_]/;

function isApplicabilityFlag(name: string): name is ApplicabilityFlag {
  return (APPLICABILITY_FLAGS as readonly string[]).includes(name);
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < source.length) {
    const ch = source[i] as string;

    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i += 1;
      continue;
    }

    if (ch === '(') {
      tokens.push({ type: 'lparen', value: ch, position: i });
      i += 1;
      continue;
    }

    if (ch === ')') {
      tokens.push({ type: 'rparen', value: ch, position: i });
      i += 1;
      continue;
    }

    if (ch === '!') {
      tokens.push({ type: 'not', value: ch, position: i });
      i += 1;
      continue;
    }

    if (ch === '&') {
      if (source[i + 1] !== '&') {
        throw new ContextExpressionError('Expected "&&"', source, i);
      }
      tokens.push({ type: 'and', value: '&&', position: i });
      i += 2;
      continue;
    }

    if (ch === '|') {
      if (source[i + 1] !== '|') {
        throw new ContextExpressionError('Expected "||"', source, i);
      }
      tokens.push({ type: 'or', value: '||', position: i });
      i += 2;
      continue;
    }

    if (IDENT_START.test(ch)) {
      const start = i;
      while (i < source.length && IDENT_PART.test(source[i] as string)) {
        i += 1;
      }
      tokens.push({ type: 'ident', value: source.slice(start, i), position: start });
      continue;
    }

    throw new ContextExpressionError(`Unexpected character "${ch}"`, source, i);
  }

  tokens.push({ type: 'eof', value: '', position: source.length });
  return tokens;
}

/**
 * Parse an applicability condition into an AST.
 *
 * Throws {@link ContextExpressionError} on anything it cannot resolve, including an
 * identifier that is not one of the four applicability flags — which is how the
 * "hostingModel is not an applicability flag" correction (§6.1) stays enforced.
 */
export function parseContextCondition(source: string): ContextExpr {
  const trimmed = source.trim();
  if (trimmed.length === 0) {
    throw new ContextExpressionError('Empty condition — use null for "always applies"', source, 0);
  }

  const tokens = tokenize(trimmed);
  let cursor = 0;

  const peek = (): Token => tokens[cursor] as Token;

  const advance = (): Token => {
    const token = tokens[cursor] as Token;
    if (token.type !== 'eof') {
      cursor += 1;
    }
    return token;
  };

  function parseOr(): ContextExpr {
    let left = parseAnd();
    while (peek().type === 'or') {
      advance();
      const right = parseAnd();
      left = { kind: 'or', left, right };
    }
    return left;
  }

  function parseAnd(): ContextExpr {
    let left = parseUnary();
    while (peek().type === 'and') {
      advance();
      const right = parseUnary();
      left = { kind: 'and', left, right };
    }
    return left;
  }

  function parseUnary(): ContextExpr {
    if (peek().type === 'not') {
      advance();
      return { kind: 'not', operand: parseUnary() };
    }
    return parsePrimary();
  }

  function parsePrimary(): ContextExpr {
    const token = advance();

    if (token.type === 'lparen') {
      const inner = parseOr();
      const closing = peek();
      if (closing.type !== 'rparen') {
        throw new ContextExpressionError('Expected ")"', trimmed, closing.position);
      }
      advance();
      return inner;
    }

    if (token.type === 'ident') {
      if (!isApplicabilityFlag(token.value)) {
        throw new ContextExpressionError(
          `"${token.value}" is not an applicability flag (allowed: ${APPLICABILITY_FLAGS.join(', ')})`,
          trimmed,
          token.position,
        );
      }
      return { kind: 'flag', name: token.value };
    }

    throw new ContextExpressionError(
      `Expected a flag name, "!" or "(" but found "${token.value || 'end of expression'}"`,
      trimmed,
      token.position,
    );
  }

  const expr = parseOr();
  const trailing = peek();
  if (trailing.type !== 'eof') {
    throw new ContextExpressionError(
      `Unexpected "${trailing.value}" after a complete expression`,
      trimmed,
      trailing.position,
    );
  }

  return expr;
}

/** Evaluate a parsed condition against a project's context profile. */
export function evaluateContextExpr(expr: ContextExpr, profile: ContextProfile): boolean {
  switch (expr.kind) {
    case 'flag':
      return profile[expr.name];
    case 'not':
      return !evaluateContextExpr(expr.operand, profile);
    case 'and':
      return evaluateContextExpr(expr.left, profile) && evaluateContextExpr(expr.right, profile);
    case 'or':
      return evaluateContextExpr(expr.left, profile) || evaluateContextExpr(expr.right, profile);
  }
}

/**
 * Render an AST back to source. Used by the item editor and by the importer's
 * round-trip assertion; parentheses are emitted wherever precedence needs them.
 */
export function serializeContextExpr(expr: ContextExpr): string {
  switch (expr.kind) {
    case 'flag':
      return expr.name;
    case 'not': {
      const inner = serializeContextExpr(expr.operand);
      return expr.operand.kind === 'flag' || expr.operand.kind === 'not'
        ? `!${inner}`
        : `!(${inner})`;
    }
    case 'and': {
      const left = expr.left.kind === 'or' ? `(${serializeContextExpr(expr.left)})` : serializeContextExpr(expr.left);
      const right =
        expr.right.kind === 'or' ? `(${serializeContextExpr(expr.right)})` : serializeContextExpr(expr.right);
      return `${left} && ${right}`;
    }
    case 'or':
      return `${serializeContextExpr(expr.left)} || ${serializeContextExpr(expr.right)}`;
  }
}

/** Every flag an expression reads — powers the item editor's "affects N projects". */
export function flagsUsed(expr: ContextExpr): ApplicabilityFlag[] {
  const found = new Set<ApplicabilityFlag>();

  const walk = (node: ContextExpr): void => {
    switch (node.kind) {
      case 'flag':
        found.add(node.name);
        break;
      case 'not':
        walk(node.operand);
        break;
      case 'and':
      case 'or':
        walk(node.left);
        walk(node.right);
        break;
    }
  };

  walk(expr);
  return [...found];
}
